<style scoped>
tr:nth-child(odd) {
  background: #f6f6f6;
}

.type-application i,
.demo-quote {
  color: #f44336;
}

.type-businessLine i {
  color: #2196f3;
}

.type-marketSegment i {
  color: #4caf50;
}
</style>

<template>
  <v-layout xs12 column wrap>
    <business-line-delete-modal
      :is-visible="isDeleteModalVisible"
      :item="selectedBusinessLine"
      :isLoading="isLoading"
      @onClose="handleModalClose"
      @onCancel="handleModalClose"
      @onDelete="handleDeleteModalDelete"
    ></business-line-delete-modal>
    <business-line-add-edit-modal
      ref="businessLineAddEditModal"
      :is-visible="isEditModalVisible || isAddModalVisible"
      :item="selectedBusinessLine"
      :isLoading="isLoading"
      @onClose="handleModalClose"
      @onSave="handleSave"
    ></business-line-add-edit-modal>
    <v-layout row wrap justify-space-between pl-4 pb-2 pt-3>
      <v-flex xs12 sm6 md4 pt-3 pr-5>
        <v-select
          v-model="demoSelectedBusinessLine"
          label="Business Line"
          :items="businessLineItems"
          item-text="name"
          return-object
        ></v-select>
      </v-flex>
      <v-flex xs12 sm6 md4 pt-3 pr-5>
        <v-select
          v-model="demoSelectedMarketSegment"
          label="Market Segment"
          :items="marketSegmentItems"
          item-text="name"
          return-object
        ></v-select>
      </v-flex>
      <v-flex xs12 sm6 md4 pt-3 pr-5>
        <v-select
          v-model="demoSelectedApplication"
          label="Application"
          :items="applicationItems"
          item-text="name"
          return-object
        ></v-select>
      </v-flex>
      <v-flex xs12>
        <v-flex xs12 sm6 md4 pt-3 pr-5></v-flex>
      </v-flex>
      <v-flex xs12></v-flex>
    </v-layout>
    <v-layout row wrap justify-space-between pl-4 pb-2 pt-3>
      <v-flex xs12 sm6 md4 pt-3 pr-5>
        <blockquote class="demo-quote">
          This is only a demo, see the contents below.
        </blockquote>
      </v-flex>
      <v-flex xs12 sm6 md4 pt-3 pr-5>
        <v-flex>
          <v-checkbox
            v-model="demoIsOptionalOemEnabled"
            label="OEM order"
            hide-details
            data-vv-name="isOptionalOemEnabled"
            data-vv-as="OEM order"
          ></v-checkbox>
        </v-flex>
        <v-select
          v-model="demoSelectedCustomization"
          label="Configurations"
          :items="customizationsList"
          item-text="label"
          item-value="id"
        ></v-select>
      </v-flex>
    </v-layout>
    <v-divider></v-divider>
    <v-layout align-center justify-space-between row>
      <v-flex xs5 sm6 pa-4>
        <v-text-field
          v-model="search"
          prepend-icon="search"
          label="Search"
          hide-details
        ></v-text-field>
      </v-flex>
      <v-flex xs4 md3 lg2 pa-4 justify-end>
        <v-btn class="ma-0" color="primary" @click="handleAdd">
          <v-icon small class="pr-1">add</v-icon>
          Create
        </v-btn>
      </v-flex>
    </v-layout>

    <v-flex xs12>
      <v-alert
        v-if="alert"
        :value="!loading"
        :type="alert.type"
        v-html="alert.message"
      ></v-alert>
      <v-data-table
        :headers="headers"
        :items="mappedItems"
        :search="search"
        :must-sort="true"
        :loading="loading"
        class="elevation-0"
        :hide-actions="hideActions"
        :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPageItems"
      >
        <template v-slot:items="{ item }">
          <tr>
            <td>
              <span :class="`type-name type-${item.type}`">
                <v-icon small class="pr-1">
                  {{
                    item.type === "businessLine"
                      ? "business"
                      : item.type === "marketSegment"
                      ? "store"
                      : "apps"
                  }}
                </v-icon>
              </span>
              {{ item.typeName }}
            </td>
            <td>
              {{ item.name }}
            </td>
            <td>{{ item.value }}</td>
            <td>{{ item.isOem ? "OEM" : "" }}</td>
            <td>
              <v-btn icon small @click="handleEdit(item)">
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn icon small @click="handleDelete(item)">
                <v-icon small>delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import BusinessLineDeleteModal from "./BusinessLineDeleteModal.vue";
import BusinessLineAddEditModal from "./BusinessLineAddEditModal.vue";
import { orderSegmentationFilter } from "@/utils/orderSegmentationFilter.js";

const paginationRowsPerPage = -1;

export default {
  mounted() {
    this.$store.dispatch("admin/fetchBusinessLinesList");
    this.$store.dispatch("admin/fetchCustomizationList");
  },
  components: {
    BusinessLineDeleteModal,
    BusinessLineAddEditModal
  },
  data() {
    return {
      search: "",
      headers: [
        {
          text: "Type",
          value: "typeName"
        },
        {
          text: "Name",
          value: "name"
        },
        {
          text: "Value",
          value: "value"
        },
        {
          text: "OEM",
          value: "isOem"
        },
        {
          text: "",
          value: "",
          sortable: false
        }
      ],
      pagination: {
        rowsPerPage: paginationRowsPerPage
      },
      rowsPerPageItems: [10, 25, 50, { text: "All", value: -1 }],

      showOem: false,

      isLoading: false,
      isDeleteModalVisible: false,
      selectedBusinessLine: undefined,

      isEditModalVisible: false,
      isAddModalVisible: false,

      demoSegmentation: {
        demoSelectedBusinessLine: undefined,
        demoSelectedMarketSegment: undefined,
        demoSelectedApplication: undefined,
        demoSelectedCustomization: undefined,
        demoIsOptionalOemEnabled: false
      }
    };
  },
  computed: {
    ...mapGetters("admin", {
      customizationsList: "getCustomizationList",
      businessLines: "getBusinessLinesList"
    }),
    ...mapState("admin", ["alert", "loading"]),
    mappedItems() {
      return this.businessLines;
    },
    hideActions() {
      return (
        !this.businessLines ||
        this.businessLines.length <= paginationRowsPerPage
      );
    },
    businessLineItems() {
      const showOem =
        this.demoSelectedCustomization === "AMAT" ||
        this.demoIsOptionalOemEnabled;

      return this.businessLines.filter(
        orderSegmentationFilter({
          isOem: showOem,
          parentId: undefined,
          type: "businessLine"
        })
      );
    },
    marketSegmentItems() {
      const businessLineId =
        this.demoSelectedBusinessLine !== undefined
          ? this.demoSelectedBusinessLine.id
          : undefined;

      return this.businessLines.filter(
        orderSegmentationFilter({
          parentId: businessLineId,
          type: "marketSegment"
        })
      );
    },
    applicationItems() {
      const marketSegmentId =
        this.demoSelectedMarketSegment !== undefined
          ? this.demoSelectedMarketSegment.id
          : undefined;

      return this.businessLines.filter(
        orderSegmentationFilter({
          parentId: marketSegmentId,
          type: "application"
        })
      );
      // return this.businessLines.filter(this.filterBusinessLines("application"));
    },
    demoSelectedBusinessLine: {
      get() {
        return this.demoSegmentation.demoSelectedBusinessLine;
      },
      set(value) {
        this.demoSegmentation.demoSelectedBusinessLine = value;
      }
    },
    demoSelectedMarketSegment: {
      get() {
        return this.demoSegmentation.demoSelectedMarketSegment;
      },
      set(value) {
        this.demoSegmentation.demoSelectedMarketSegment = value;
      }
    },
    demoSelectedApplication: {
      get() {
        return this.demoSegmentation.demoSelectedApplication;
      },
      set(value) {
        this.demoSegmentation.demoSelectedApplication = value;
      }
    },
    demoSelectedCustomization: {
      get() {
        return this.demoSegmentation.demoSelectedCustomization;
      },
      set(value) {
        this.demoSegmentation.demoSelectedCustomization = value;
      }
    },
    demoIsOptionalOemEnabled: {
      get() {
        return this.demoSegmentation.demoIsOptionalOemEnabled;
      },
      set(value) {
        this.demoSegmentation.demoIsOptionalOemEnabled = value;
      }
    }
  },
  methods: {
    handleEdit(item) {
      this.selectedBusinessLine = item;
      this.isEditModalVisible = true;
    },
    handleDelete(item) {
      this.selectedBusinessLine = item;
      this.isDeleteModalVisible = true;
    },
    handleAdd() {
      this.selectedBusinessLine = undefined;
      this.isAddModalVisible = true;
    },
    handleSave(data) {
      // Add new entry
      if (this.isAddModalVisible) {
        this.$store
          .dispatch("admin/addBusinessLine", {
            data
          })
          .then(() => {
            this.isAddModalVisible = false;
            this.$refs.businessLineAddEditModal.$refs.businessLineFormAdd.reset();
            this.selectedBusinessLine = undefined;
            this.isLoading = false;

            this.$store.commit("admin/setAlert", {
              type: "success",
              message: `Business Line "${data.businessLine.name} - ${
                data.businessLine.value
              }" was created!`
            });
          });
      }

      // Edit existing entry
      if (this.isEditModalVisible) {
        this.$store
          .dispatch("admin/editBusinessLine", {
            data
          })
          .then(() => {
            this.isEditModalVisible = false;
            this.$refs.businessLineAddEditModal.$refs.businessLineFormAdd.reset();
            this.selectedBusinessLine = undefined;
            this.isLoading = false;

            this.$store.commit("admin/setAlert", {
              type: "success",
              message: `Business Line "${data.businessLine.name} - ${
                data.businessLine.value
              }" was updated!`
            });
          });
      }

      this.handleModalClose();
    },
    handleModalClose() {
      this.isEditModalVisible = false;
      this.isAddModalVisible = false;
      this.isDeleteModalVisible = false;
      this.selectedBusinessLine = undefined;
    },
    handleDeleteModalDelete() {
      this.isLoading = true;

      this.$store
        .dispatch("admin/deleteBusinessLine", {
          data: { businessLine: this.selectedBusinessLine }
        })
        .then(() => {
          this.isDeleteModalVisible = false;

          this.$store.commit("admin/setAlert", {
            type: "error",
            message: `Business Line "${
              this.selectedBusinessLine.name
            }" was deleted!`
          });

          this.selectedBusinessLine = undefined;
          this.isLoading = false;
        });
    }
  },
  watch: {
    alert(alert) {
      if (alert && alert.message) {
        setTimeout(() => {
          this.$store.commit("admin/setAlert", null);
        }, 3000);
      }
    },
    demoSelectedBusinessLine() {
      this.demoSelectedMarketSegment = undefined;
      this.demoSelectedApplication = undefined;
    },
    demoSelectedMarketSegment() {
      this.demoSelectedApplication = undefined;
    },
    demoIsOptionalOemEnabled() {
      this.demoSelectedBusinessLine = undefined;
      this.demoSelectedMarketSegment = undefined;
      this.demoSelectedApplication = undefined;
    }
  }
};
</script>
